"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { CustomPriceFacet } from "../types/filters.type";

export interface PriceRangeOption {
  label: string;
  value: string;
}

export interface UsePriceFilterReturn {
  minValue: string;
  maxValue: string;
  selectedRangeValue: string;
  rangeOptions: PriceRangeOption[];
  handleMinChange: (formatted: string) => void;
  handleMaxChange: (formatted: string) => void;
  handleMinBlur: () => void;
  handleMaxBlur: () => void;
  selectRange: (value: string) => void;
}

const digitsOnly = (raw: string) => raw.replace(/\D/g, "");

const buildRangeValue = (min: number, max: number) => `${min}-${max}`;

const clamp = (value: number, min: number, max: number) => {
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

export function usePriceFilter(
  facet: CustomPriceFacet,
  initialValues: string[],
  onConfirm: (values: string[]) => void,
): UsePriceFilterReturn {
  const { range, fixed_ranges } = facet;

  const [minValue, setMinValue] = useState<string>(() => {
    const value = initialValues[0];
    return value ? String(Number(value)) : String(range.min);
  });

  const [maxValue, setMaxValue] = useState<string>(() => {
    const value = initialValues[1];
    return value ? String(Number(value)) : String(range.max);
  });

  const isFirstRender = useRef(true);
  const initialValuesLength = initialValues.length;

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (initialValuesLength === 0) {
      setMinValue(String(range.min));
      setMaxValue(String(range.max));
    }
  }, [initialValuesLength, range.min, range.max]);

  const rangeOptions = useMemo<PriceRangeOption[]>(
    () =>
      fixed_ranges.map((r) => ({
        label: r.label,
        value: buildRangeValue(r.min, r.max),
      })),
    [fixed_ranges],
  );

  const selectedRangeValue = useMemo(() => {
    const minNum = minValue ? Number(minValue) : Number.NaN;
    const maxNum = maxValue ? Number(maxValue) : Number.NaN;

    if (Number.isNaN(minNum) || Number.isNaN(maxNum)) return "";

    const match = fixed_ranges.find(
      (r) =>
        clamp(r.min, range.min, range.max) === minNum &&
        clamp(r.max, range.min, range.max) === maxNum,
    );

    return match ? buildRangeValue(match.min, match.max) : "";
  }, [minValue, maxValue, fixed_ranges, range.min, range.max]);

  const handleMinChange = useCallback((formatted: string) => {
    setMinValue(digitsOnly(formatted));
  }, []);

  const handleMaxChange = useCallback((formatted: string) => {
    setMaxValue(digitsOnly(formatted));
  }, []);

  const handleMinBlur = useCallback(() => {
    let finalMin: number;

    if (!minValue) {
      finalMin = range.min;
    } else {
      const raw = Number(minValue);
      if (raw > range.max) {
        finalMin = range.min;
      } else {
        finalMin = Math.max(raw, range.min);
        if (maxValue) {
          const maxNum = Number(maxValue);
          if (finalMin > maxNum) finalMin = maxNum;
        }
      }
    }

    setMinValue(String(finalMin));
    const currentMax = maxValue ? Number(maxValue) : range.max;
    onConfirm([String(finalMin), String(currentMax)]);
  }, [minValue, maxValue, range.min, range.max, onConfirm]);

  const handleMaxBlur = useCallback(() => {
    let finalMax: number;

    if (!maxValue) {
      finalMax = range.max;
    } else {
      finalMax = clamp(Number(maxValue), range.min, range.max);
      if (minValue) {
        const minNum = Number(minValue);
        if (finalMax < minNum) finalMax = minNum;
      }
    }

    setMaxValue(String(finalMax));
    const currentMin = minValue ? Number(minValue) : range.min;
    onConfirm([String(currentMin), String(finalMax)]);
  }, [minValue, maxValue, range.min, range.max, onConfirm]);

  const selectRange = useCallback(
    (value: string) => {
      const match = fixed_ranges.find(
        (r) => buildRangeValue(r.min, r.max) === value,
      );
      if (!match) return;
      const clampedMin = clamp(match.min, range.min, range.max);
      const clampedMax = clamp(match.max, range.min, range.max);
      setMinValue(String(clampedMin));
      setMaxValue(String(clampedMax));
      onConfirm([String(clampedMin), String(clampedMax)]);
    },
    [fixed_ranges, range.min, range.max, onConfirm],
  );

  return {
    minValue,
    maxValue,
    selectedRangeValue,
    rangeOptions,
    handleMinChange,
    handleMaxChange,
    handleMinBlur,
    handleMaxBlur,
    selectRange,
  };
}
